import { HTMLAttributes, forwardRef } from 'react';

type BadgeVariant =
  | 'primary'
  | 'success'
  | 'warning'
  | 'error'
  | 'info'
  | 'neutral'
  | 'hunter'
  | 'gatherer'
  | 'purple'
  | 'pink';

type BadgeSize = 'xs' | 'sm' | 'md' | 'lg';

interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  variant?: BadgeVariant;
  size?: BadgeSize;
  dot?: boolean;
}

const variantClasses: Record<BadgeVariant, string> = {
  primary: 'bg-primary-500/20 text-primary-700 dark:text-primary-400 border border-primary-500/30',
  success: 'bg-success-500/20 text-success-700 dark:text-success-400 border border-success-500/30',
  warning: 'bg-warning-500/20 text-warning-700 dark:text-warning-400 border border-warning-500/30',
  error: 'bg-error-500/20 text-error-700 dark:text-error-400 border border-error-500/30',
  info: 'bg-info-500/20 text-info-700 dark:text-info-400 border border-info-500/30',
  neutral: 'bg-surface text-text-tag border border-border',
  hunter: 'bg-hunter-500/20 text-hunter-700 dark:text-hunter-100 border border-hunter-500/30',
  gatherer: 'bg-gatherer-500/20 text-gatherer-700 dark:text-gatherer-100 border border-gatherer-500/30',
  purple: 'bg-purple-500/20 text-purple-700 dark:text-purple-300 border border-purple-500/30',
  pink: 'bg-pink-500/20 text-pink-700 dark:text-pink-400 border border-pink-500/30',
};

const sizeClasses: Record<BadgeSize, string> = {
  xs: 'px-1.5 py-0.5 text-2xs',
  sm: 'px-2 py-0.5 text-xs',
  md: 'px-2.5 py-1 text-sm',
  lg: 'px-3 py-1 text-base',
};

export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(
  ({ variant = 'neutral', size = 'sm', dot = false, children, className = '', ...props }, ref) => {
    return (
      <span
        ref={ref}
        className={`
          inline-flex items-center gap-2
          font-medium rounded-full
          transition-colors duration-200
          ${variantClasses[variant]}
          ${sizeClasses[size]}
          ${className}
        `}
        {...props}
      >
        {dot && (
          <span
            className={`
              w-1.5 h-1.5 rounded-full
              ${
                variant === 'primary'
                  ? 'bg-primary-400'
                  : variant === 'success'
                  ? 'bg-success-400'
                  : variant === 'warning'
                  ? 'bg-warning-400'
                  : variant === 'error'
                  ? 'bg-error-400'
                  : variant === 'hunter'
                  ? 'bg-hunter-400'
                  : variant === 'gatherer'
                  ? 'bg-gatherer-400'
                  : variant === 'purple'
                  ? 'bg-purple-400'
                  : variant === 'pink'
                  ? 'bg-pink-400'
                  : 'bg-primary-400'
              }
            `}
          />
        )}
        {children}
      </span>
    );
  }
);

Badge.displayName = 'Badge';
